import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  alpha,
  Slider,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeMutedIcon,
  VolumeDown as VolumeDownIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Generate a loud alert sound using Web Audio API
const generateAlertSound = (frequency = 1200, duration = 600, waveType = 'sine') => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.type = waveType;
  oscillator.frequency.value = frequency;
  
  // Envelope: attack, sustain, release
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  gainNode.gain.linearRampToValueAtTime(0.7, audioContext.currentTime + 0.05);
  gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration / 1000);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration / 1000);
};

// Create a more complex alert pattern (dual frequency)
const playAlertPattern = (volume = 0.7) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Pattern: beep, pause, beep, pause, longer beep
  const beepPattern = [
    { frequency: 800, duration: 150, delay: 0 },
    { frequency: 1000, duration: 150, delay: 200 },
    { frequency: 900, duration: 300, delay: 400 },
  ];
  
  beepPattern.forEach(({ frequency, duration, delay }) => {
    setTimeout(() => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + duration / 1000);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration / 1000);
    }, delay);
  });
};

const NotificationSystem = ({ notifications = [], onRemove }) => {
  const [localNotifications, setLocalNotifications] = useState([]);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const audioContextRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize audio context on first user interaction
  useEffect(() => {
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
    };
    
    document.addEventListener('click', initAudio, { once: true });
    return () => document.removeEventListener('click', initAudio);
  }, []);

  // Add auto-dismiss timer and play sound
  useEffect(() => {
    const updatedNotifications = notifications.map(notif => ({
      ...notif,
      dismissAt: Date.now() + 8000,
    }));
    
    setLocalNotifications(updatedNotifications);
    
    // Play sound for each new notification
    if (notifications.length > 0 && !isMuted && audioContextRef.current) {
      try {
        playAlertPattern(volume);
      } catch (err) {
        console.log('Audio playback not available:', err);
      }
    }
  }, [notifications, isMuted, volume]);

  // Auto-dismiss timer
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setLocalNotifications(prev => 
        prev.filter(notif => {
          if (notif.dismissAt <= now) {
            onRemove(notif.id);
            return false;
          }
          return true;
        })
      );
    }, 100);

    return () => clearInterval(interval);
  }, [onRemove]);

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 20 }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'success': return '#00ff88';
      case 'error': return '#ff4757';
      case 'warning': return '#ffa502';
      case 'info':
      default: return '#00d4ff';
    }
  };

  const handleClose = (id) => {
    setLocalNotifications(prev => prev.filter(n => n.id !== id));
    setTimeout(() => onRemove(id), 300);
  };

  const handleVolumeChange = (e, newValue) => {
    setVolume(newValue);
    setIsMuted(false);
    // Play test sound
    try {
      playAlertPattern(newValue);
    } catch (err) {
      console.log('Audio playback not available');
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const getVolumeIcon = () => {
    if (isMuted) return <VolumeMutedIcon />;
    if (volume < 0.3) return <VolumeDownIcon />;
    return <VolumeUpIcon />;
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: isMobile ? 12 : 24,
        right: isMobile ? 12 : 24,
        left: isMobile ? 12 : 'auto',
        zIndex: 2000,
        maxWidth: isMobile ? 'calc(100% - 24px)' : 400,
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1.5,
        pointerEvents: 'none',
      }}
    >
      {/* Volume Control */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        style={{
          pointerEvents: 'auto',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 1.5,
            background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: alpha('#666', 0.3),
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
            <IconButton
              size="small"
              onClick={toggleMute}
              sx={{
                color: isMuted ? '#ff4757' : '#00d4ff',
                '&:hover': {
                  bgcolor: alpha('#fff', 0.1),
                }
              }}
            >
              {getVolumeIcon()}
            </IconButton>
          </Tooltip>
          
          <Slider
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            min={0}
            max={1}
            step={0.1}
            sx={{
              width: isMobile ? 80 : 120,
              color: '#00d4ff',
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: '#00d4ff',
                boxShadow: '0 0 8px rgba(0, 212, 255, 0.5)',
              },
              '& .MuiSlider-track': {
                borderRadius: 4,
              },
              '& .MuiSlider-rail': {
                backgroundColor: alpha('#fff', 0.1),
              }
            }}
          />
          
          <Typography
            variant="caption"
            sx={{
              color: alpha('#fff', 0.6),
              minWidth: 30,
              textAlign: 'right',
              fontFamily: 'monospace',
            }}
          >
            {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
          </Typography>
        </Paper>
      </motion.div>

      {/* Notifications */}
      <AnimatePresence mode="popLayout">
        {localNotifications.map((notification, index) => {
          const color = getNotificationColor(notification.type);
          const timeLeft = Math.max(0, notification.dismissAt - Date.now());
          const progress = (timeLeft / 8000) * 100;
          
          return (
            <motion.div
              key={notification.id}
              layout
              initial={{ 
                opacity: 0,
                x: isMobile ? 0 : 400,
                scale: 0.8,
              }}
              animate={{ 
                opacity: 1,
                x: 0,
                scale: 1,
              }}
              exit={{ 
                opacity: 0,
                x: isMobile ? 0 : 400,
                scale: 0.8,
                transition: { duration: 0.3 }
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 40,
                mass: 0.8,
              }}
              style={{
                pointerEvents: 'auto',
              }}
            >
              <Paper
                elevation={8}
                sx={{
                  p: 2,
                  background: 'linear-gradient(135deg, rgba(15, 20, 40, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: alpha(color, 0.3),
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: `0 8px 32px ${alpha(color, 0.2)}`,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                    animation: 'notification-glow 2s infinite',
                  }
                }}
              >
                {/* Progress Bar */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: alpha(color, 0.1),
                  }}
                >
                  <Box
                    sx={{
                      width: `${progress}%`,
                      height: '100%',
                      bgcolor: color,
                      transition: 'width 0.1s linear',
                      boxShadow: `0 0 8px ${alpha(color, 0.8)}`,
                    }}
                  />
                </Box>

                {/* Content */}
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      bgcolor: alpha(color, 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: alpha(color, 0.3),
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(getNotificationIcon(notification.type), {
                      sx: { color }
                    })}
                  </Box>

                  {/* Message */}
                  <Box flex={1} minWidth={0}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#fff', 
                        fontWeight: 600,
                        mb: 0.5,
                        wordBreak: 'break-word',
                      }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: alpha('#fff', 0.7),
                        display: 'block',
                        wordBreak: 'break-word',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    
                    {/* Additional Data */}
                    {(notification.strategy || notification.signal || notification.price) && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {notification.strategy && (
                          <Chip
                            label={notification.strategy}
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: alpha('#00d4ff', 0.2),
                              color: '#00d4ff',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {notification.signal && (
                          <Chip
                            label={notification.signal}
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: alpha('#9333ea', 0.2),
                              color: '#9333ea',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {notification.price && (
                          <Chip
                            icon={<MoneyIcon sx={{ fontSize: 12 }} />}
                            label={`${notification.price.toFixed(2)}`}
                            size="small"
                            sx={{
                              height: 20,
                              bgcolor: alpha('#ffa502', 0.2),
                              color: '#ffa502',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              '& .MuiChip-icon': {
                                color: '#ffa502',
                              }
                            }}
                          />
                        )}
                      </Stack>
                    )}
                  </Box>

                  {/* Close Button */}
                  <IconButton
                    size="small"
                    onClick={() => handleClose(notification.id)}
                    sx={{
                      color: alpha('#fff', 0.5),
                      flexShrink: 0,
                      '&:hover': {
                        color: '#fff',
                        bgcolor: alpha('#fff', 0.1),
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Paper>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Global Styles */}
      <style>
        {`
          @keyframes notification-glow {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
};

export default NotificationSystem;
